/* =============================================================================
   DelightPaw — theme.js
   Vanilla JS, no dependencies. Everything is namespaced under window.DelightPaw.
   ========================================================================== */
(function () {
  'use strict';

  var DP = window.DelightPaw = window.DelightPaw || {};
  DP.settings = DP.settings || {};

  /* ---------------------------------------------------------------------
     Utilities
     --------------------------------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 200);
    };
  }

  function formatMoney(cents) {
    var format = DP.settings.moneyFormat || '${{amount}}';
    var value = (cents / 100);

    function withComma(num, decimals) {
      var parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    function withDot(num, decimals) {
      var parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(',');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return withComma(value, 2);
        case 'amount_no_decimals': return withComma(value, 0);
        case 'amount_with_comma_separator': return withDot(value, 2);
        case 'amount_no_decimals_with_comma_separator': return withDot(value, 0);
        case 'amount_with_apostrophe_separator':
          return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        default: return withComma(value, 2);
      }
    });
  }
  DP.formatMoney = formatMoney;

  /* ---------------------------------------------------------------------
     Toast
     --------------------------------------------------------------------- */
  var toastTimer;
  function toast(message) {
    var el = $('#dp-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'dp-toast';
      el.className = 'toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg><span></span>';
    $('span', el).textContent = message;
    requestAnimationFrame(function () { el.classList.add('is-visible'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 2600);
  }
  DP.toast = toast;

  /* ---------------------------------------------------------------------
     Drawer controller (mobile menu, search, cart)
     --------------------------------------------------------------------- */
  var Drawers = (function () {
    var openId = null;
    var lastFocused = null;

    function overlay() { return $('#dp-overlay'); }

    function open(id) {
      var el = document.getElementById(id);
      if (!el) return;
      if (openId && openId !== id) closeAll(true);
      lastFocused = document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      if (overlay()) overlay().classList.add('is-open');
      document.body.classList.add('is-locked');
      openId = id;
      var focusTarget = el.querySelector('[data-drawer-focus]') || el.querySelector('button, a, input');
      if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 320);
    }

    function closeAll(silent) {
      $$('.drawer').forEach(function (d) {
        d.classList.remove('is-open');
        d.setAttribute('aria-hidden', 'true');
      });
      if (overlay()) overlay().classList.remove('is-open');
      document.body.classList.remove('is-locked');
      openId = null;
      if (!silent && lastFocused) lastFocused.focus();
    }

    function init() {
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-drawer-open]');
        if (opener) {
          e.preventDefault();
          open(opener.getAttribute('data-drawer-open'));
          return;
        }
        if (e.target.closest('[data-drawer-close]') || e.target.id === 'dp-overlay') {
          e.preventDefault();
          closeAll();
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && openId) closeAll();
      });
    }

    return { init: init, open: open, closeAll: closeAll };
  })();
  DP.Drawers = Drawers;

  /* ---------------------------------------------------------------------
     Cart — AJAX add, drawer rendering, quantity changes
     --------------------------------------------------------------------- */
  var Cart = (function () {
    var state = null;

    function routes() {
      return DP.settings.routes || {
        cart_add_url: '/cart/add',
        cart_change_url: '/cart/change',
        cart_url: '/cart'
      };
    }

    function updateBubble(count) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count > 99 ? '99+' : count;
        el.hidden = count === 0;
      });
    }

    function freeShipThreshold() {
      return parseInt(DP.settings.freeShippingThreshold, 10) || 0;
    }

    function renderFreeShip(total) {
      var threshold = freeShipThreshold();
      if (!threshold) return '';
      var remaining = threshold - total;
      var pct = Math.min(100, Math.round((total / threshold) * 100));
      var text = remaining > 0
        ? "You're <strong>" + formatMoney(remaining) + '</strong> away from free shipping!'
        : '<strong>Nice! You’ve unlocked free shipping. 🎉</strong>';
      return '<div class="free-ship-bar">' +
        '<p class="free-ship-bar__text">' + text + '</p>' +
        '<div class="free-ship-bar__track"><div class="free-ship-bar__fill" style="width:' + pct + '%"></div></div>' +
        '</div>';
    }

    function lineHtml(item, index) {
      var img = item.image
        ? '<img src="' + item.image.replace(/(\.[^.]*)$/, '_160x160_crop_center$1') + '" alt="' + escapeAttr(item.product_title) + '" loading="lazy" width="82" height="82">'
        : '<div style="width:100%;height:100%;background:var(--dp-sand)"></div>';

      var variant = '';
      if (item.options_with_values && item.options_with_values.length) {
        var parts = item.options_with_values.filter(function (o) {
          return o.value && o.value !== 'Default Title';
        }).map(function (o) { return o.value; });
        if (parts.length) variant = '<div class="cart-line__variant">' + escapeHtml(parts.join(' · ')) + '</div>';
      }

      var priceHtml = '<span class="cart-line__price">' + formatMoney(item.final_line_price) + '</span>';
      if (item.original_line_price > item.final_line_price) {
        priceHtml = '<span class="cart-line__price price--sale">' + formatMoney(item.final_line_price) +
          ' <s class="price__compare">' + formatMoney(item.original_line_price) + '</s></span>';
      }

      return '<div class="cart-line" data-line="' + (index + 1) + '">' +
        '<a class="cart-line__media" href="' + item.url + '">' + img + '</a>' +
        '<div>' +
          '<a class="cart-line__title" href="' + item.url + '">' + escapeHtml(item.product_title) + '</a>' +
          variant +
          '<div class="cart-line__foot">' +
            '<div class="qty" data-line-qty>' +
              '<button type="button" data-line-change="' + (index + 1) + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">' + minusIcon() + '</button>' +
              '<input type="number" value="' + item.quantity + '" min="0" data-line-input="' + (index + 1) + '" aria-label="Quantity">' +
              '<button type="button" data-line-change="' + (index + 1) + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">' + plusIcon() + '</button>' +
            '</div>' +
            priceHtml +
          '</div>' +
          '<button type="button" class="cart-line__remove" data-line-change="' + (index + 1) + '" data-qty="0">Remove</button>' +
        '</div>' +
      '</div>';
    }

    function minusIcon() { return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>'; }
    function plusIcon() { return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'; }

    function escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function escapeAttr(str) { return escapeHtml(str); }

    function render(cart) {
      state = cart;
      updateBubble(cart.item_count);

      var body = $('#dp-cart-body');
      var foot = $('#dp-cart-foot');
      if (!body || !foot) return;

      if (!cart.item_count) {
        body.innerHTML =
          '<div class="cart-empty">' +
            '<div class="cart-empty__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg></div>' +
            '<h3>Your cart is empty</h3>' +
            '<p style="color:var(--dp-muted)">Let’s find something your pet will love.</p>' +
            '<a class="btn" href="' + (DP.settings.routes && DP.settings.routes.all_products_collection_url || '/collections/all') + '">Start shopping</a>' +
          '</div>';
        foot.hidden = true;
        return;
      }

      foot.hidden = false;
      body.innerHTML = cart.items.map(lineHtml).join('');
      foot.innerHTML =
        renderFreeShip(cart.total_price) +
        '<div class="cart-total"><span>Subtotal</span><strong>' + formatMoney(cart.total_price) + '</strong></div>' +
        '<p class="cart-note">Taxes and shipping calculated at checkout.</p>' +
        '<div style="display:grid;gap:9px">' +
          '<a class="btn btn--full" href="' + routes().cart_url + '/checkout">Checkout · ' + formatMoney(cart.total_price) + '</a>' +
          '<a class="btn btn--outline btn--full" href="' + routes().cart_url + '">View cart</a>' +
        '</div>';
    }

    function fetchCart() {
      return fetch(routes().cart_url + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { render(cart); return cart; });
    }

    function add(formData, button) {
      if (button) button.classList.add('is-loading');
      return fetch(routes().cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (button) button.classList.remove('is-loading');
          if (!res.ok) {
            toast(res.data.description || res.data.message || 'Could not add to cart.');
            return null;
          }
          return fetchCart().then(function () {
            if (DP.settings.openCartOnAdd !== false) {
              Drawers.open('dp-cart-drawer');
            } else {
              toast('Added to cart');
            }
            return res.data;
          });
        })
        .catch(function () {
          if (button) button.classList.remove('is-loading');
          toast('Something went wrong. Please try again.');
        });
    }

    function change(line, quantity) {
      var body = $('#dp-cart-body');
      if (body) body.style.opacity = '.55';
      return fetch(routes().cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (body) body.style.opacity = '';
          render(cart);
          if (document.body.classList.contains('template-cart')) window.location.reload();
        });
    }

    function init() {
      // Add-to-cart forms
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('form[data-ajax-cart]');
        if (!form) return;
        e.preventDefault();
        add(new FormData(form), form.querySelector('[type="submit"]'));
      });

      // Quick add buttons on product cards
      document.addEventListener('click', function (e) {
        var quick = e.target.closest('[data-quick-add]');
        if (quick) {
          e.preventDefault();
          var id = quick.getAttribute('data-quick-add');
          if (!id) return;
          var fd = new FormData();
          fd.append('id', id);
          fd.append('quantity', 1);
          add(fd, quick);
          return;
        }

        var changer = e.target.closest('[data-line-change]');
        if (changer) {
          e.preventDefault();
          change(parseInt(changer.getAttribute('data-line-change'), 10),
                 parseInt(changer.getAttribute('data-qty'), 10));
        }
      });

      document.addEventListener('change', function (e) {
        var input = e.target.closest('[data-line-input]');
        if (!input) return;
        change(parseInt(input.getAttribute('data-line-input'), 10), parseInt(input.value, 10) || 0);
      });

      // Standalone quantity steppers (product page)
      document.addEventListener('click', function (e) {
        var step = e.target.closest('[data-qty-step]');
        if (!step) return;
        e.preventDefault();
        var wrap = step.closest('.qty');
        var input = $('input', wrap);
        if (!input) return;
        var dir = parseInt(step.getAttribute('data-qty-step'), 10);
        var min = parseInt(input.getAttribute('min'), 10) || 1;
        var next = (parseInt(input.value, 10) || min) + dir;
        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if ($('#dp-cart-body')) fetchCart();
    }

    return { init: init, add: add, fetch: fetchCart, render: render };
  })();
  DP.Cart = Cart;

  /* ---------------------------------------------------------------------
     Sticky header
     --------------------------------------------------------------------- */
  function initHeader() {
    var header = $('#dp-header');
    if (!header) return;
    var toggle = function () {
      header.classList.toggle('is-stuck', window.scrollY > 8);
    };
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Announcement rotator
     --------------------------------------------------------------------- */
  function initAnnouncements() {
    var wrap = $('[data-announcements]');
    if (!wrap) return;
    var items = $$('.announcement__item', wrap);
    if (items.length < 2) return;
    var i = 0;
    setInterval(function () {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, parseInt(wrap.getAttribute('data-announcements'), 10) || 5000);
  }

  /* ---------------------------------------------------------------------
     Accordions
     --------------------------------------------------------------------- */
  function initAccordions() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.accordion__trigger');
      if (!trigger) return;
      var item = trigger.closest('.accordion__item');
      var isOpen = item.classList.contains('is-open');
      var group = trigger.closest('[data-accordion-single]');
      if (group && !isOpen) {
        $$('.accordion__item', group).forEach(function (i) {
          i.classList.remove('is-open');
          var t = $('.accordion__trigger', i);
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  function initReveal() {
    var els = $$('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay'), 10) || 0;
        setTimeout(function () { el.classList.add('is-visible'); }, delay);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Product gallery
     --------------------------------------------------------------------- */
  function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;
    var slides = $$('.gallery__slide', gallery);
    var thumbs = $$('.gallery__thumb', gallery);
    if (!slides.length) return;

    function show(index) {
      index = (index + slides.length) % slides.length;
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-selected', String(i === index));
      });
      gallery.setAttribute('data-active', index);
    }
    DP.galleryShow = show;

    thumbs.forEach(function (t, i) { on(t, 'click', function () { show(i); }); });
    on($('[data-gallery-prev]', gallery), 'click', function () {
      show((parseInt(gallery.getAttribute('data-active'), 10) || 0) - 1);
    });
    on($('[data-gallery-next]', gallery), 'click', function () {
      show((parseInt(gallery.getAttribute('data-active'), 10) || 0) + 1);
    });

    // Swipe support
    var startX = null;
    on(gallery, 'touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    on(gallery, 'touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 45) {
        show((parseInt(gallery.getAttribute('data-active'), 10) || 0) + (dx < 0 ? 1 : -1));
      }
      startX = null;
    });

    // Jump to a media id (used by the variant picker)
    DP.galleryShowMedia = function (mediaId) {
      var idx = slides.findIndex(function (s) { return s.getAttribute('data-media-id') === String(mediaId); });
      if (idx > -1) show(idx);
    };
  }

  /* ---------------------------------------------------------------------
     Variant picker
     --------------------------------------------------------------------- */
  function initVariants() {
    var root = $('[data-variant-picker]');
    if (!root) return;
    var dataEl = $('#dp-variant-data');
    if (!dataEl) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

    var idInput = $('[data-variant-id]', root.closest('form') || document);
    var priceWrap = $('[data-price-wrap]');
    var addBtn = $('[data-add-button]');
    var addLabel = addBtn ? $('.btn__label', addBtn) : null;
    var stockLine = $('[data-stock-line]');
    var skuEl = $('[data-sku]');

    function selectedOptions() {
      return $$('[data-option-index]', root).map(function (group) {
        var checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
    }

    function matchVariant(opts) {
      return variants.find(function (v) {
        return opts.every(function (o, i) { return v.options[i] === o; });
      });
    }

    function updateAvailability(opts) {
      $$('[data-option-index]', root).forEach(function (group) {
        var index = parseInt(group.getAttribute('data-option-index'), 10);
        $$('input', group).forEach(function (input) {
          var test = opts.slice();
          test[index] = input.value;
          // Only test options up to and including this one for partial matching
          var possible = variants.some(function (v) {
            return v.available && test.every(function (o, i) {
              return i === index ? v.options[i] === o : (o === null || v.options[i] === o);
            });
          });
          // Never disable the option the shopper currently has selected —
          // otherwise a dead-end combination leaves the group looking broken.
          input.disabled = input.checked ? false : !possible;
        });
      });
    }

    function update() {
      var opts = selectedOptions();
      var variant = matchVariant(opts);
      updateAvailability(opts);

      $$('[data-option-selected]', root).forEach(function (el) {
        var index = parseInt(el.getAttribute('data-option-selected'), 10);
        el.textContent = opts[index] || '';
      });

      if (!variant) {
        if (addBtn) { addBtn.disabled = true; }
        if (addLabel) addLabel.textContent = 'Unavailable';
        if (stockLine) stockLine.innerHTML = '';
        return;
      }

      if (idInput) idInput.value = variant.id;

      // URL sync (keeps share links accurate)
      if (history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }

      if (priceWrap && variant.price_html) priceWrap.innerHTML = variant.price_html;
      if (skuEl) skuEl.textContent = variant.sku || '';

      if (addBtn) {
        addBtn.disabled = !variant.available;
        if (addLabel) addLabel.textContent = variant.available ? (addBtn.getAttribute('data-add-text') || 'Add to cart') : 'Sold out';
      }

      if (stockLine) {
        if (!variant.available) {
          stockLine.className = 'stock-line stock-line--out';
          stockLine.innerHTML = '<span class="stock-dot"></span> Currently out of stock';
        } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 10) {
          stockLine.className = 'stock-line stock-line--low';
          stockLine.innerHTML = '<span class="stock-dot"></span> Only ' + variant.inventory_quantity + ' left — selling fast';
        } else {
          stockLine.className = 'stock-line';
          stockLine.innerHTML = '<span class="stock-dot"></span> In stock, ready to ship';
        }
      }

      if (variant.featured_media_id && DP.galleryShowMedia) DP.galleryShowMedia(variant.featured_media_id);
    }

    on(root, 'change', update);
    update();
  }

  /* ---------------------------------------------------------------------
     Collection filtering & sorting (client-side refinement)
     --------------------------------------------------------------------- */
  function initShopFilters() {
    var root = $('[data-shop]');
    if (!root) return;

    var grid = $('[data-shop-grid]', root);
    var cards = $$('[data-product-card]', grid);
    var countEl = $('[data-shop-count]', root);
    var emptyEl = $('[data-shop-empty]', root);
    var chipsEl = $('[data-active-filters]', root);
    var searchInput = $('[data-shop-search]', root);
    var sortSelect = $('[data-client-sort]', root);
    var priceMin = $('[data-price-min]', root);
    var priceMax = $('[data-price-max]', root);
    var priceSlider = $('[data-price-slider]', root);

    var originalOrder = cards.slice();

    function activeRatings() {
      return $$('[data-filter-rating]:checked', root).map(function (i) { return parseFloat(i.value); });
    }
    function activeTags() {
      return $$('[data-filter-tag]:checked', root).map(function (i) { return i.value.toLowerCase(); });
    }
    function activeAvailability() {
      return $$('[data-filter-availability]:checked', root).map(function (i) { return i.value; });
    }

    function apply() {
      var q = (searchInput && searchInput.value || '').trim().toLowerCase();
      var ratings = activeRatings();
      var tags = activeTags();
      var avail = activeAvailability();
      var min = priceMin && priceMin.value !== '' ? parseFloat(priceMin.value) : null;
      var max = priceMax && priceMax.value !== '' ? parseFloat(priceMax.value) : null;

      var shown = 0;

      cards.forEach(function (card) {
        var title = (card.getAttribute('data-title') || '').toLowerCase();
        var cardTags = (card.getAttribute('data-tags') || '').toLowerCase();
        var type = (card.getAttribute('data-type') || '').toLowerCase();
        var price = parseFloat(card.getAttribute('data-price')) || 0;
        var rating = parseFloat(card.getAttribute('data-rating')) || 0;
        var available = card.getAttribute('data-available') === 'true';

        var ok = true;

        if (q && title.indexOf(q) === -1 && cardTags.indexOf(q) === -1 && type.indexOf(q) === -1) ok = false;
        if (ok && min !== null && price < min) ok = false;
        if (ok && max !== null && price > max) ok = false;
        if (ok && ratings.length && !ratings.some(function (r) { return rating >= r; })) ok = false;
        if (ok && tags.length && !tags.every(function (t) { return cardTags.split('|').indexOf(t) > -1; })) ok = false;
        if (ok && avail.length) {
          var wantIn = avail.indexOf('in-stock') > -1;
          if (wantIn && !available) ok = false;
        }

        card.hidden = !ok;
        if (ok) shown++;
      });

      if (countEl) {
        countEl.textContent = shown + (shown === 1 ? ' product' : ' products');
      }
      if (emptyEl) emptyEl.hidden = shown !== 0;
      if (grid) grid.hidden = shown === 0;

      renderChips(q, ratings, tags, min, max);
    }

    function renderChips(q, ratings, tags, min, max) {
      if (!chipsEl) return;
      var chips = [];
      if (q) chips.push({ label: '“' + q + '”', type: 'search' });
      ratings.forEach(function (r) { chips.push({ label: r + '★ & up', type: 'rating', value: r }); });
      tags.forEach(function (t) { chips.push({ label: t.replace(/(^|\s)\S/g, function (c) { return c.toUpperCase(); }), type: 'tag', value: t }); });
      if (min !== null || max !== null) {
        chips.push({ label: (min !== null ? '$' + min : '$0') + ' – ' + (max !== null ? '$' + max : 'any'), type: 'price' });
      }

      chipsEl.innerHTML = chips.map(function (c) {
        return '<span class="chip">' + c.label +
          '<button type="button" data-clear-filter="' + c.type + '" data-value="' + (c.value === undefined ? '' : c.value) + '" aria-label="Remove filter">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button></span>';
      }).join('') + (chips.length > 1 ? '<button type="button" class="chip" data-clear-filter="all" style="background:var(--dp-sand);color:var(--dp-ink-2);padding-right:14px">Clear all</button>' : '');
    }

    function sort(mode) {
      var list = originalOrder.slice();
      if (mode === 'rating-desc') {
        list.sort(function (a, b) {
          return (parseFloat(b.getAttribute('data-rating')) || 0) - (parseFloat(a.getAttribute('data-rating')) || 0);
        });
      } else if (mode === 'reviews-desc') {
        list.sort(function (a, b) {
          return (parseInt(b.getAttribute('data-reviews'), 10) || 0) - (parseInt(a.getAttribute('data-reviews'), 10) || 0);
        });
      } else if (mode === 'discount-desc') {
        list.sort(function (a, b) {
          return (parseFloat(b.getAttribute('data-discount')) || 0) - (parseFloat(a.getAttribute('data-discount')) || 0);
        });
      } else {
        list = originalOrder.slice();
      }
      list.forEach(function (card) { grid.appendChild(card); });
    }

    on(searchInput, 'input', debounce(apply, 180));
    $$('[data-filter-rating], [data-filter-tag], [data-filter-availability]', root).forEach(function (input) {
      on(input, 'change', apply);
    });
    on(priceMin, 'input', debounce(apply, 260));
    on(priceMax, 'input', debounce(apply, 260));
    on(priceSlider, 'input', function () {
      if (priceMax) priceMax.value = priceSlider.value;
      apply();
    });

    on(sortSelect, 'change', function () {
      var value = sortSelect.value;
      if (value.indexOf('client:') === 0) {
        sort(value.replace('client:', ''));
      } else {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }
    });

    on(root, 'click', function (e) {
      var clear = e.target.closest('[data-clear-filter]');
      if (!clear) return;
      var type = clear.getAttribute('data-clear-filter');
      var value = clear.getAttribute('data-value');
      if (type === 'search' || type === 'all') { if (searchInput) searchInput.value = ''; }
      if (type === 'rating' || type === 'all') {
        $$('[data-filter-rating]', root).forEach(function (i) {
          if (type === 'all' || i.value === value) i.checked = false;
        });
      }
      if (type === 'tag' || type === 'all') {
        $$('[data-filter-tag]', root).forEach(function (i) {
          if (type === 'all' || i.value.toLowerCase() === value) i.checked = false;
        });
      }
      if (type === 'price' || type === 'all') {
        if (priceMin) priceMin.value = '';
        if (priceMax) priceMax.value = '';
        if (priceSlider) priceSlider.value = priceSlider.max;
      }
      if (type === 'all') {
        $$('[data-filter-availability]', root).forEach(function (i) { i.checked = false; });
      }
      apply();
    });

    // Mobile filter panel
    var facets = $('#dp-facets', root);
    var overlayEl = $('#dp-overlay');

    function openFacets() {
      if (!facets) return;
      facets.classList.add('is-open');
      if (overlayEl) overlayEl.classList.add('is-open');
      document.body.classList.add('is-locked');
    }
    function closeFacets() {
      if (!facets) return;
      facets.classList.remove('is-open');
      if (overlayEl) overlayEl.classList.remove('is-open');
      document.body.classList.remove('is-locked');
    }

    on($('[data-facets-toggle]', root), 'click', openFacets);
    on($('[data-facets-close]', root), 'click', closeFacets);
    on(overlayEl, 'click', closeFacets);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeFacets();
    });

    // Facet group collapse
    $$('.facets__title', root).forEach(function (title) {
      on(title, 'click', function () {
        var group = title.closest('.facets__group');
        if (group) group.classList.toggle('is-collapsed');
      });
    });

    apply();
  }

  /* ---------------------------------------------------------------------
     Newsletter / contact form feedback (works with Shopify's form)
     --------------------------------------------------------------------- */
  function initFormStates() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-loading-form]');
      if (!form) return;
      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.classList.add('is-loading');
    });
  }

  /* ---------------------------------------------------------------------
     Wishlist (localStorage — visual only, no account required)
     --------------------------------------------------------------------- */
  function initWishlist() {
    var KEY = 'delightpaw:wishlist';
    var list;
    try { list = JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { list = []; }

    function paint() {
      $$('[data-wish]').forEach(function (btn) {
        btn.classList.toggle('is-active', list.indexOf(btn.getAttribute('data-wish')) > -1);
      });
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-wish');
      var i = list.indexOf(id);
      if (i > -1) { list.splice(i, 1); toast('Removed from wishlist'); }
      else { list.push(id); toast('Saved to wishlist ❤'); }
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (err) {}
      paint();
    });

    paint();
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() {
    Drawers.init();
    Cart.init();
    initHeader();
    initAnnouncements();
    initAccordions();
    initReveal();
    initGallery();
    initVariants();
    initShopFilters();
    initFormStates();
    initWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init after theme editor section reloads
  document.addEventListener('shopify:section:load', function () {
    initReveal();
    initGallery();
    initVariants();
    initShopFilters();
    initAnnouncements();
  });
})();
